import React, { SelectHTMLAttributes } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  options?: SelectOption[];
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  onChange: (value: string) => void;
  children?: React.ReactNode;
}

const Select: React.FC<SelectProps> = ({
  label,
  options,
  helperText,
  error,
  fullWidth = false,
  className,
  id,
  value,
  onChange,
  children,
  ...props
}) => {
  const selectId = id || Math.random().toString(36).substring(2, 9);
  const widthClass = fullWidth ? 'w-full' : '';

  const baseSelectClasses = 'rounded-md border-2 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium bg-white text-black dark:bg-gray-800 dark:text-white border-gray-400 dark:border-gray-500';
  const errorSelectClasses = error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : '';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className={`${widthClass}`}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-semibold text-gray-900 dark:text-white mb-1"
        >
          {label}
        </label>
      )}
      <select
        id={selectId}
        className={`${baseSelectClasses} ${errorSelectClasses} ${className || ''} ${widthClass}`}
        value={value}
        onChange={handleChange}
        {...props}
      >
        {options ? (
          options.map((option) => (
            <option key={option.value} value={option.value} className="bg-white text-black dark:bg-gray-800 dark:text-white">
              {option.label}
            </option>
          ))
        ) : (
          children
        )}
      </select>
      {helperText && !error && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-200 font-medium">{helperText}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400 font-medium">{error}</p>
      )}
    </div>
  );
};

export default Select;